import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Gauge, LogOut, Settings as SettingsIcon } from "lucide-react";
import { post, setUnauthenticatedHandler } from "./api/client";
import { prefetchEvents, useCalendars, useLiveUpdates, useMe } from "./api/hooks";
import type { Me } from "./api/types";
import { addDays, addMonths } from "./lib/date";
import { newDraft, draftFromEvent } from "./lib/draft";
import { UiProvider, daysForView, useUi, type Anchor } from "./state/ui";
import { AppNav } from "./components/AppNav";
import { CalendarPage } from "./components/CalendarPage";
import { LoginPage } from "./components/LoginPage";
import { SettingsPage } from "./components/SettingsPage";
import { HomePage } from "./components/HomePage";
import { TasksPage } from "./components/TasksPage";
import { ResolutionsPage } from "./components/ResolutionsPage";
import { AdminPage } from "./components/AdminPage";
import { HealthPage } from "./components/HealthPage";
import { FinancePage } from "./components/FinancePage";
import { CyclePage } from "./components/CyclePage";
import { NavTabs } from "./components/NavTabs";
import { ShortcutsHelp } from "./components/ShortcutsHelp";
import { TimeGridView } from "./components/views/TimeGridView";
import { MonthView } from "./components/views/MonthView";
import { YearView } from "./components/views/YearView";
import { ScheduleView } from "./components/views/ScheduleView";
import { SearchView } from "./components/views/SearchView";
import { EventDetails, useDeleteWithUndo } from "./components/overlays/EventDetails";
import { QuickCreate } from "./components/overlays/QuickCreate";
import { EventEditor } from "./components/overlays/EventEditor";
import { ScopeDialog } from "./components/overlays/ScopeDialog";
import { Popover } from "./components/ui/Popover";

function useTheme(theme: Me["settings"]["theme"] | undefined) {
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = theme === "dark" || ((theme ?? "system") === "system" && media.matches);
      document.documentElement.dataset.theme = dark ? "dark" : "light";
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);
}

export function App() {
  const qc = useQueryClient();
  const me = useMe();
  const [loggedOut, setLoggedOut] = useState(false);
  const onLogin = useCallback(() => {
    setLoggedOut(false);
    void qc.resetQueries();
  }, [qc]);

  useEffect(() => setUnauthenticatedHandler(() => setLoggedOut(true)), []);
  useTheme(me.data?.settings.theme);

  if (loggedOut || window.location.pathname.startsWith("/entrar") || (me.isError && !me.data)) {
    return <LoginPage onLoggedIn={onLogin} />;
  }
  if (!me.data) {
    return (
      <div className="center-fill">
        <div className="spinner" />
      </div>
    );
  }
  return <Authenticated me={me.data} />;
}

function Authenticated({ me }: { me: Me }) {
  const calendars = useCalendars();
  useLiveUpdates(true);
  if (!calendars.data) {
    return (
      <div className="center-fill">
        <div className="spinner" />
      </div>
    );
  }
  return (
    <UiProvider me={me} calendars={calendars.data}>
      <Shell />
    </UiProvider>
  );
}

function CurrentView() {
  const ui = useUi();
  if (ui.page === "home") return <HomePage />;
  if (ui.page === "tasks") return <TasksPage />;
  if (ui.page === "resolutions") return <ResolutionsPage />;
  if (ui.page === "health") return <HealthPage />;
  if (ui.page === "finance") return <FinancePage />;
  if (ui.page === "cycle") return <CyclePage />;
  if (ui.page === "settings") return <SettingsPage />;
  if (ui.page === "admin") return ui.me.isAdmin ? <AdminPage /> : <HomePage />;
  if (ui.page === "search") return <SearchView />;
  return (
    <CalendarPage>
      {ui.view === "month" ? <MonthView /> : ui.view === "year" ? <YearView /> : ui.view === "schedule" ? <ScheduleView /> : <TimeGridView key={ui.view} />}
    </CalendarPage>
  );
}

/** Prefetches the previous and next periods so arrow navigation is instant. */
function usePrefetchNeighbors() {
  const ui = useUi();
  const qc = useQueryClient();
  useEffect(() => {
    if (ui.page !== "calendar" || ui.view === "year" || ui.view === "schedule") return;
    const { weekStart, showWeekends } = ui.me.settings;
    for (const dir of [-1, 1]) {
      const anchor =
        ui.view === "month" ? addMonths(ui.date, dir) : addDays(ui.date, dir * (ui.view === "day" ? 1 : ui.view === "4days" ? 4 : 7));
      const days = daysForView(ui.view, anchor, weekStart, showWeekends);
      void prefetchEvents(qc, days[0], addDays(days[days.length - 1], 1));
    }
  }, [ui.view, ui.date, ui.page, ui.me.settings, qc]);
}

function useShortcuts() {
  const ui = useUi();
  const deleteEvent = useDeleteWithUndo();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.ctrlKey || e.metaKey || e.altKey || t.closest("input, textarea, select, [contenteditable]")) return;
      const overlay = ui.overlay;
      if (overlay?.kind === "details") {
        if (e.key === "e") ui.setOverlay({ kind: "editor", draft: draftFromEvent(overlay.event), event: overlay.event });
        if (e.key === "Delete" || e.key === "Backspace") void deleteEvent(overlay.event);
        return;
      }
      if (overlay) return;
      const views: Record<string, Parameters<typeof ui.setView>[0]> = { d: "day", w: "week", m: "month", y: "year", a: "schedule", x: "4days" };
      if (views[e.key]) ui.setView(views[e.key]);
      else if (e.key === "t") ui.goToday();
      else if ((e.key === "j" || e.key === "n") && ui.page === "calendar") ui.step(1);
      else if ((e.key === "k" || e.key === "p") && ui.page === "calendar") ui.step(-1);
      else if (e.key === "c") ui.setOverlay({ kind: "editor", draft: newDraft({ date: ui.date, startMin: 9 * 60, settings: ui.me.settings, calendars: ui.calendars }) });
      else if (e.key === "s") ui.setPage("settings");
      else if (e.key === "?") ui.setOverlay({ kind: "shortcuts" });
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ui, deleteEvent]);
}

function Shell() {
  const ui = useUi();
  const [account, setAccount] = useState<Anchor | null>(null);
  useShortcuts();
  usePrefetchNeighbors();
  const overlay = ui.overlay;

  return (
    <div className="app">
      <AppNav onAccount={setAccount} />
      <main className="app-view">
        <CurrentView />
      </main>
      <NavTabs />

      {overlay?.kind === "details" && <EventDetails key={overlay.event.key} event={overlay.event} anchor={overlay.anchor} />}
      {overlay?.kind === "quick" && <QuickCreate key={`${overlay.anchor.x}:${overlay.anchor.y}`} draft={overlay.draft} anchor={overlay.anchor} />}
      {overlay?.kind === "editor" && <EventEditor draft={overlay.draft} event={overlay.event} />}
      {overlay?.kind === "scope" && <ScopeDialog mode={overlay.mode} onChoose={overlay.onChoose} onCancel={overlay.onCancel} />}
      {overlay?.kind === "shortcuts" && <ShortcutsHelp />}

      {account && (
        <Popover anchor={account} placement="below" onClose={() => setAccount(null)} width={280}>
          <div className="account-head">
            <div className="eyebrow">Conta</div>
            <div className="account-name display">{ui.me.name ?? "Você"}</div>
            <div className="account-phone">{ui.me.phone}</div>
          </div>
          <div style={{ padding: 6, borderTop: "1px solid var(--line-soft)" }}>
            <button className="menu-item" onClick={() => { ui.setPage("settings"); setAccount(null); }}>
              <SettingsIcon size={18} /> Configurações
            </button>
            {ui.me.isAdmin && (
              <button className="menu-item" onClick={() => { ui.setPage("admin"); setAccount(null); }}>
                <Gauge size={18} /> Painel admin
              </button>
            )}
            <button
              className="menu-item"
              onClick={async () => {
                await post("/api/v1/auth/logout");
                window.location.href = "/entrar";
              }}
            >
              <LogOut size={18} /> Sair
            </button>
          </div>
        </Popover>
      )}

      {ui.toast && (
        <div className="toast" role="status">
          <span>{ui.toast.text}</span>
          {ui.toast.action && (
            <button
              className="btn text"
              onClick={() => {
                ui.toast?.action?.run();
                ui.showToast("Ação desfeita");
              }}
            >
              {ui.toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

